import { AgGridReact } from 'ag-grid-react';
import { InputAdornment, Typography } from '@mui/material';
import { PersonAddOutlined, ToggleOff, ToggleOn } from '@mui/icons-material';
import { useLazyQuery } from '@apollo/client';
import { useState, useEffect, useMemo, useContext, useRef, useCallback } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import SearchIcon from '@mui/icons-material/Search';
import TextField from '@mui/material/TextField';

import { GET_SHARE_PROJECT } from '../../apis/project';
import { GET_WORKSPACE_INVITES, GET_WORKSPACE_PROFILES } from '../../apis/workspace';
import Button from '../../components/atoms/button';
import InviteUserModal from './InviteUserModal';
import ProjectContext from '../../context/ProjectContext';
import ProjectWarningContext from '../../context/ProjectWarningContext';
import ROLES from '../../constants/roles';
import Toaster from '../../components/Snackbar/Toaster';
import useAuth from '../../context/AuthContext';
import useLoader from '../../context/LoaderContext';
import useProjectVersionHistory from '../../context/VersionHistoryContext';
import UserTableActions from './UserTableActions';
import UserTableAvatar from './UserTableAvatar';
import UserTableName from './UserTableName';
import UserTableRole from './UserTableRole';
import UserTableStatus from './UserTableStatus';
import UserTableTooltip from './UserTableTooltip';
import useWorkspace from '../../context/WorkspaceContext';
import WorkspaceOptions from '../../components/molecules/workspace-options';
import LoadingOverlay from '../../components/LoadingOverlay';

const CONTAINER_STYLE = { width: '100%', height: '100%' };
const GRID_STYLE = { height: '100%', width: '100%' };

const lastActiveFormat = (lastActive) => {
  const lastActiveDate = moment(parseInt(lastActive, 10));
  const now = moment();

  if (now.diff(lastActiveDate, 'months') > 1) {
    return lastActiveDate.format('lll');
  }

  const lastActiveFromNow = lastActiveDate.fromNow();

  if (now.diff(lastActiveDate, 'days') > 1) {
    return `${lastActiveFromNow} at ${lastActiveDate.format('LT')}`;
  }

  return lastActiveDate.fromNow();
};

const mapInvites = (invites) => invites.map((row) => ({
  id: row.id,
  type: 'invite',
  alldata: row,
  displayName: '-',
  emailAddress: row.email,
  role: row.role,
  lastActive: '-',
  status: moment(parseInt(row.updatedAt, 10)).fromNow(),
}));

const mapProfiles = (profiles, userId, sharedProjects) => profiles.map((row) => {
  const userProjects = sharedProjects.filter((project) => project.workspaceProfileId === row.id);
  return {
    type: 'user',
    id: row.id,
    alldata: row,
    displayName: row.displayName,
    emailAddress: row.userWorkspace.email,
    logo: row.logo,
    googleId: row.googleId,
    uId: row.userId,
    role: ROLES[row.roleId].value,
    projects: userProjects,
    // eslint-disable-next-line no-nested-ternary
    lastActive: row.id === userId ? 'Active' : row.lastActive ? lastActiveFormat(row.lastActive) : 'Never logged in',
    status: row.isDisabled ? 'Disabled' : 'Active',
  };
});

const UserList = () => {
  // init
  const { isLoading: isDataLoading, setIsLoading: setIsDataLoading } = useLoader();
  const { workspace, workspaceProfile } = useWorkspace();
  const { authUser } = useAuth();
  const { dispatch } = useContext(ProjectWarningContext);
  const { setShowVersionHistoryBar, setSelectedListItem, setReadOnlyMode } = useProjectVersionHistory();
  const { dispatch: projectDispatch } = useContext(ProjectContext);

  // states
  const [isLoading, setIsLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [gridApi, setGridApi] = useState();
  const [displayInvitedUsers, setDisplayInvitedUsers] = useState(true);
  const [toast, setToast] = useState({ open: false, message: '', autoHideDuration: 3000 });
  const [refreshTable, setRefreshTable] = useState(false);

  // mutations/queries
  const [getWorkspaceUsers] = useLazyQuery(GET_WORKSPACE_PROFILES, { fetchPolicy: 'network-only' });
  const [getWorkspaceInvites] = useLazyQuery(GET_WORKSPACE_INVITES, { fetchPolicy: 'network-only' });
  const [getSharedProjects] = useLazyQuery(GET_SHARE_PROJECT, { fetchPolicy: 'network-only' });

  // constants
  const gridRef = useRef(null);
  const defaultColDef = useMemo(() => ({ resizable: false, suppressMovable: true }), []);
  const gridOptions = useMemo(() => ({ suppressNoRowsOverlay: true, rowHeight: 60, headerHeight: 40, defaultColDef: { resizable: true }, getRowId: (params) => params.data.id }), [rowData]);
  const columnDefs = useMemo(() => [
    { headerName: '', field: 'logo', cellRenderer: UserTableAvatar, flex: 1.3, wrapText: true },
    { headerName: 'User Name', field: 'userName', cellRenderer: UserTableName, flex: 3, wrapText: true },
    { headerName: 'Email Address', field: 'emailAddress', flex: 5, wrapText: true },
    { headerName: 'Role', field: 'role', cellRenderer: UserTableRole, cellRendererParams: { setIsLoading, setToast }, flex: 4, wrapText: true },
    { headerName: 'Skillset', field: 'skillset', hide: true, flex: 4, wrapText: true },
    { headerName: 'Project', field: 'project', cellRenderer: UserTableTooltip, flex: 3, wrapText: true },
    { headerName: 'Last Active', field: 'lastActive', flex: 4, wrapText: true },
    { headerName: 'Status', field: 'status', cellRenderer: UserTableStatus, flex: 4, wrapText: true },
    { headerName: 'Actions', field: 'actions', cellRenderer: UserTableActions, cellRendererParams: { setToast, setRefreshTable }, flex: 4, wrapText: true },
  ], []);

  const onGridReady = useCallback((gridParams) => {
    setGridApi(gridParams.api);
  }, [rowData]);

  const handleToastClose = () => {
    setToast((prevstate) => ({ ...prevstate, open: false }));
  };

  const handleOnSuccess = async (newInvites) => {
    if (newInvites?.length) {
      const newInvitesList = mapInvites(newInvites);
      gridApi.applyTransaction({ add: newInvitesList });
    }
  };

  const onFilterTextBoxChanged = (e) => {
    const dataFilter = e.target.value;
    gridApi.setQuickFilter(dataFilter);
  };

  const toggleDisplayInvitedUsers = () => {
    setDisplayInvitedUsers(!displayInvitedUsers);
  };

  const getProfilesAndInvites = async () => {
    if (authUser && workspace && workspaceProfile) {
      document.title = `${workspace.name} | All Users`;
      try {
        if (!isDataLoading) setIsDataLoading(true);
        const [workspaceProfilesData, workspaceInvitesData, sharedProjectData] = await Promise.all([
          getWorkspaceUsers({ variables: { workspaceId: workspace.id } }),
          getWorkspaceInvites({ variables: { workspaceId: workspace.id } }),
          getSharedProjects({ variables: { input: { workspaceId: workspace.id } } }),
        ]);

        const { data: { listWorkspaceProfiles: workspaceProfiles }, loading: profileLoading, error: profileError } = workspaceProfilesData;
        const { data: { listWorkspaceInvites: workspaceInvites }, loading: invitesLoading, error: invitesError } = workspaceInvitesData;
        const { data: { getSharedProjects: workspaceSharedProjects } } = sharedProjectData;
        let profiles = [];
        let invites = [];

        if (!profileLoading && !profileError && workspaceProfiles) {
          profiles = mapProfiles(workspaceProfiles, workspaceProfile.id, workspaceSharedProjects);
        }

        if (!invitesError && !invitesLoading && workspaceInvites) {
          invites = mapInvites(workspaceInvites);
        }

        setRowData(profiles.concat(...invites));
      } catch (error) {
        setToast((prevstate) => ({ ...prevstate, message: 'Something went wrong while listing users!', open: true, type: 'error' }));
      } finally {
        setIsDataLoading(false);
      }
    }
  };

  useEffect(() => {
    projectDispatch({ type: 'SET_PROJECT_ID', payload: { projectId: null } });
    dispatch({ type: 'SHOW_RISK_BAR', payload: { showRiskBar: false } });
    setShowVersionHistoryBar(false);
    setReadOnlyMode(false);
    setSelectedListItem(null);
    return () => setRowData([]);
  }, [workspace]);

  useEffect(() => {
    setRowData([]);
    getProfilesAndInvites();
  }, [refreshTable, workspace, workspaceProfile, authUser]);

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.onFilterChanged();
    }
  }, [displayInvitedUsers]);

  const showInvitedUsers = useCallback((node) => {
    if (node.data) {
      return !displayInvitedUsers && node.data.type !== 'invite';
    }
    return true;
  }, [displayInvitedUsers]);

  const getCurrentInvitesAndUsersCount = useCallback(() => {
    if (gridApi && !gridApi.destroyCalled) {
      const renderedNodes = gridApi.getRenderedNodes();
      return renderedNodes.length ? renderedNodes.length : rowData.length;
    }
    return 0;
  }, [gridApi, rowData]);

  const getRowClass = (params) => {
    if (params.data.alldata.isDisabled) {
      return 'disabled-row';
    }
    return '';
  };

  return (
    <>
      <LoadingOverlay open={isLoading} />
      <Toaster anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} onClose={handleToastClose} {...toast} />
      <div className="page-header">
        <WorkspaceOptions />
        <Typography className="title-project">Users</Typography>
        <div className="header-layout">
          <div className="header-search-and-text-wrap" style={{ gap: 10 }}>
            <div className="top-search-btn">
              <TextField
                className="search-input"
                type="search"
                onChange={onFilterTextBoxChanged}
                placeholder="Search"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" style={{ color: 'rgba(0, 0, 0, 0.4)' }}>
                      <SearchIcon className="search-icon" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <Button onClick={toggleDisplayInvitedUsers} data-on={displayInvitedUsers} className="invited-user-toggle">
              <PersonAddOutlined fontSize="small" />
              <strong><h5 style={{ margin: 0 }}>Invited</h5></strong>
              {displayInvitedUsers ? <ToggleOn /> : <ToggleOff />}
            </Button>
          </div>
          {workspace && <div> <InviteUserModal existingUsers={getCurrentInvitesAndUsersCount()} setToast={setToast} onSuccess={handleOnSuccess} /></div>}
        </div>
      </div>

      {
        !isDataLoading &&
        <div style={CONTAINER_STYLE}>
          <div style={{ height: '100%', width: '100%', boxSizing: 'border-box' }}>
            <div style={GRID_STYLE} className="ag-theme-alpine">
              <AgGridReact
                ref={gridRef}
                className="ag-theme-custom-react common-table-grid user-table-grid"
                gridOptions={gridOptions}
                rowBuffer
                rowData={rowData}
                columnDefs={columnDefs}
                domLayout="autoHeight"
                animateRows
                onGridReady={onGridReady}
                isExternalFilterPresent={() => !displayInvitedUsers}
                doesExternalFilterPass={showInvitedUsers}
                debounceVerticalScrollbar
                defaultColDef={defaultColDef}
                getRowClass={getRowClass}
              />
            </div>
          </div>
        </div>
      }
    </>
  );
};
UserList.propTypes = PropTypes.any.isRequired;
export default UserList;
